import { useState } from 'react'
import { Button, Modal, Table } from '@mantine/core'
import { IconCheck, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'
import dayjs from 'dayjs'
import type { AuditAccessEvent } from '@behuman/core-contract'
import { AccessSourceBadge, ActorAvatar } from './AuditBadges'
import {
  ACCESS_SOURCE_LABELS,
  formatAuditDate,
  formatAuditTime,
  RESULT_LABELS,
  sectionSlugLabel,
} from './auditModel'

type Props = {
  events: AuditAccessEvent[]
}

/**
 * Access History table: USER / DATE & TIME / ACTIVITY / SOURCE, with an
 * expandable per-row detail band (device, IP address, access type, result).
 * The mockup's ACTIVE TIME column was cut from the MVP with the rest of
 * duration tracking (Alex, 2026-07-30).
 */
export function AccessHistoryTable({ events }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // QA round 1 (W6) — the full-event modal; the mockup control used to be a dead
  // disabled button.
  const [fullEvent, setFullEvent] = useState<AuditAccessEvent | null>(null)

  if (events.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        No access events recorded for the selected filters.
      </div>
    )
  }

  return (
    <>
      <Table verticalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              User
            </Table.Th>
            <Table.Th className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Date & time
            </Table.Th>
            <Table.Th className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Activity
            </Table.Th>
            <Table.Th className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Source
            </Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {events.map((event) => {
            const expanded = expandedId === event.id
            return [
              <Table.Tr
                key={event.id}
                className="cursor-pointer"
                onClick={() => setExpandedId(expanded ? null : event.id)}
                data-testid="access-event-row"
              >
                <Table.Td>
                  <div className="flex items-center gap-3">
                    <ActorAvatar name={event.actorName} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{event.actorName}</div>
                      {event.actorRole && (
                        <div className="text-xs text-gray-500">{event.actorRole}</div>
                      )}
                    </div>
                  </div>
                </Table.Td>
                <Table.Td>
                  <div className="text-sm font-medium text-gray-900">
                    {formatAuditDate(event.recordedAt)}
                  </div>
                  <div className="text-xs text-gray-500">{formatAuditTime(event.recordedAt)}</div>
                </Table.Td>
                <Table.Td>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-900">{event.activity}</div>
                    {/* A refused access must be legible without opening the row. The
                        result lived only in the expanded detail band, so a failure sat in
                        the list looking exactly like a success — and nobody expands ten
                        thousand rows to find the one that was denied. Success stays
                        unmarked: it is the overwhelming majority, and a badge on every row
                        would bury the exceptions this is for. */}
                    {event.result !== 'success' && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                        <IconX size={11} />
                        {RESULT_LABELS[event.result]}
                      </span>
                    )}
                  </div>
                  {event.sections.length > 0 && (
                    // QA round 3 (independent re-test) — names, not dataKey slugs:
                    // 'Labs/Imaging', not 'labs-imaging', matching Change History.
                    <div className="text-xs text-gray-500">
                      {event.sections.map(sectionSlugLabel).join(', ')}
                    </div>
                  )}
                </Table.Td>
                <Table.Td>
                  <AccessSourceBadge source={event.source} />
                </Table.Td>
                <Table.Td>
                  {/* The disclosure as a real button, not just a clickable row. The row
                      keeps its onClick for the mouse, but a <tr> is not focusable and
                      takes no keypress, so before this the per-event detail band — device,
                      IP, access type, result, clinic, session, and the View full event
                      control inside it — could not be reached from the keyboard at all.
                      A button here rather than roles on the <tr>, so the table keeps its
                      table semantics for a screen reader. */}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} details for ${event.activity} by ${event.actorName}`}
                    className="flex items-center rounded text-gray-400 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:outline-none"
                    // The click also bubbles to the row's own handler, which is harmless:
                    // both read `expanded` from this same render and set the same value.
                    onClick={() => setExpandedId(expanded ? null : event.id)}
                  >
                    {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                  </button>
                </Table.Td>
              </Table.Tr>,
              expanded ? (
                <Table.Tr key={`${event.id}-detail`} data-testid="access-event-detail">
                  <Table.Td colSpan={5} className="bg-gray-50">
                    <div className="flex items-start justify-between px-2 py-1">
                      <div className="grid grid-cols-4 gap-x-8 gap-y-4">
                        <DetailField label="Device" value={event.device ?? '—'} />
                        <DetailField label="IP address" value={event.ipAddress ?? '—'} />
                        <DetailField
                          label="Access type"
                          value={
                            event.accessType === 'emergency'
                              ? 'Emergency access'
                              : 'Standard access'
                          }
                        />
                        <div>
                          <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Result
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-sm text-gray-900">
                            {event.result === 'success' ? (
                              <IconCheck size={14} className="text-emerald-600" />
                            ) : (
                              <IconX size={14} className="text-red-500" />
                            )}
                            {RESULT_LABELS[event.result]}
                          </div>
                        </div>
                        {/* S22 — the ticket's "User role and clinic/organization". The role
                          rides in the User column; the clinic belongs here with the rest
                          of the per-event metadata. AC8's clinic filter is in the audit
                          filters drawer. */}
                        <DetailField
                          label="Clinic / organization"
                          value={event.actorOrganization ?? '—'}
                        />
                        {/* S17 — the ticket's "Session ID": the Medplum Login id, derived
                          server-side from the access token's login_id claim. */}
                        <DetailField label="Session ID" value={event.sessionId ?? '—'} />
                      </div>
                      {/* QA round 1 (W6) — the ticket's "View details must show the
                        complete metadata": every contract field, in a modal. */}
                      <Button variant="subtle" size="xs" onClick={() => setFullEvent(event)}>
                        View full event
                      </Button>
                    </div>
                  </Table.Td>
                </Table.Tr>
              ) : null,
            ]
          })}
        </Table.Tbody>
      </Table>
      {fullEvent && <FullEventModal event={fullEvent} onClose={() => setFullEvent(null)} />}
    </>
  )
}

/**
 * QA round 1 (W6) — the COMPLETE event record, every contract field
 * (auditAccessEventSchema): actor (name/role/reference), clinic/organization (name and
 * reference), session, activity and sections, source, device, IP, access type, result,
 * the download/export marker, the record and patient ids, and the timestamp in BOTH
 * UTC and the reader's local time. Absent optional fields render as an em dash —
 * "the record does not say" must be visible, not skipped.
 */
function FullEventModal({ event, onClose }: { event: AuditAccessEvent; onClose: () => void }) {
  const rows: Array<[string, string]> = [
    ['Event ID', event.id],
    ['Patient ID', event.patientId ?? '—'],
    ['Recorded (UTC)', dayjs(event.recordedAt).toISOString()],
    [
      'Recorded (local)',
      `${formatAuditDate(event.recordedAt)} ${formatAuditTime(event.recordedAt)}`,
    ],
    ['Actor', event.actorName],
    ['Actor role', event.actorRole ?? '—'],
    ['Actor reference', event.actorReference ?? '—'],
    ['Clinic / organization', event.actorOrganization ?? '—'],
    ['Organization reference', event.actorOrganizationReference ?? '—'],
    ['Session ID', event.sessionId ?? '—'],
    ['Activity', event.activity],
    ['Sections', event.sections.length > 0 ? event.sections.map(sectionSlugLabel).join(', ') : '—'],
    ['Source', ACCESS_SOURCE_LABELS[event.source]],
    ['Device', event.device ?? '—'],
    ['IP address', event.ipAddress ?? '—'],
    ['Access type', event.accessType === 'emergency' ? 'Emergency access' : 'Standard access'],
    ['Result', RESULT_LABELS[event.result]],
    ['Download or export', event.download ? 'Yes' : 'No'],
  ]
  return (
    <Modal opened onClose={onClose} title="Full event" size="lg" centered>
      <dl data-testid="full-event-metadata" className="grid grid-cols-[220px_1fr] gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{label}</dt>
            <dd className="break-all text-gray-900">{value}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  )
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{label}</div>
      <div className="mt-1 text-sm text-gray-900">{value}</div>
    </div>
  )
}
